import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Loader2, Bell } from 'lucide-react';
import { NotificationPreferences } from '../../lib/api';

interface NotificationsTabProps {
  notifPrefs: NotificationPreferences | null;
  loadingNotifPrefs: boolean;
  savingNotifPrefs: boolean;
  onPrefChange: (key: keyof NotificationPreferences, value: boolean) => void;
}

export function NotificationsTab({
  notifPrefs,
  loadingNotifPrefs,
  savingNotifPrefs,
  onPrefChange,
}: NotificationsTabProps) {
  if (loadingNotifPrefs) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading notification preferences...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Purchase Order Notifications
          </CardTitle>
          <CardDescription>
            Get notified about purchase order updates and delivery status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive notifications within the application</p>
            </div>
            <Switch
              checked={notifPrefs?.po_updates_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('po_updates_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email when PO status changes</p>
            </div>
            <Switch
              checked={notifPrefs?.po_updates_email ?? true}
              onCheckedChange={(checked) => onPrefChange('po_updates_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Delivery Notifications</CardTitle>
          <CardDescription>
            Stay informed about upcoming deliveries and delays.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">Get notified about delivery updates</p>
            </div>
            <Switch
              checked={notifPrefs?.delivery_notifications_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('delivery_notifications_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email for delivery alerts</p>
            </div>
            <Switch
              checked={notifPrefs?.delivery_notifications_email ?? true}
              onCheckedChange={(checked) => onPrefChange('delivery_notifications_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Quality Alerts (NCR)</CardTitle>
          <CardDescription>
            Get notified about quality issues and NCR updates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>In-App Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive NCR alerts in the application</p>
            </div>
            <Switch
              checked={notifPrefs?.quality_alerts_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('quality_alerts_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive email for quality alerts</p>
            </div>
            <Switch
              checked={notifPrefs?.quality_alerts_email ?? true}
              onCheckedChange={(checked) => onPrefChange('quality_alerts_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Supplier & RFQ Notifications</CardTitle>
          <CardDescription>
            Stay updated on supplier activity and RFQ responses.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Supplier Updates (In-App)</Label>
              <p className="text-sm text-muted-foreground">Rating changes, qualifications</p>
            </div>
            <Switch
              checked={notifPrefs?.supplier_updates_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('supplier_updates_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>RFQ Notifications (In-App)</Label>
              <p className="text-sm text-muted-foreground">New RFQs and responses</p>
            </div>
            <Switch
              checked={notifPrefs?.rfq_notifications_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('rfq_notifications_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>RFQ Notifications (Email)</Label>
              <p className="text-sm text-muted-foreground">Receive email for RFQ activity</p>
            </div>
            <Switch
              checked={notifPrefs?.rfq_notifications_email ?? true}
              onCheckedChange={(checked) => onPrefChange('rfq_notifications_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other Notifications</CardTitle>
          <CardDescription>
            Documents, mentions, and system announcements.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Document Notifications</Label>
              <p className="text-sm text-muted-foreground">New uploads and expiring documents</p>
            </div>
            <Switch
              checked={notifPrefs?.document_notifications_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('document_notifications_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Mention Notifications (Email)</Label>
              <p className="text-sm text-muted-foreground">Get emailed when you&apos;re mentioned</p>
            </div>
            <Switch
              checked={notifPrefs?.mention_notifications_email ?? true}
              onCheckedChange={(checked) => onPrefChange('mention_notifications_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>System Announcements</Label>
              <p className="text-sm text-muted-foreground">Important system updates</p>
            </div>
            <Switch
              checked={notifPrefs?.system_announcements_in_app ?? true}
              onCheckedChange={(checked) => onPrefChange('system_announcements_in_app', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Weekly Reports (Email)</Label>
              <p className="text-sm text-muted-foreground">Receive weekly summary reports</p>
            </div>
            <Switch
              checked={notifPrefs?.weekly_reports_email ?? false}
              onCheckedChange={(checked) => onPrefChange('weekly_reports_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Daily Digest (Email)</Label>
              <p className="text-sm text-muted-foreground">Receive daily notification summary</p>
            </div>
            <Switch
              checked={notifPrefs?.daily_digest_email ?? false}
              onCheckedChange={(checked) => onPrefChange('daily_digest_email', checked)}
              disabled={savingNotifPrefs}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
